'use client';

import type { MultichainExploitResult } from '@revoke.cash/core/exploits';
import { deduplicateArray } from '@revoke.cash/core/utils';
import { formatDate } from '@revoke.cash/core/utils/time';
import ChainLogo from 'components/common/ChainLogo';
import Href from 'components/common/Href';
import MarkdownProse from 'components/common/MarkdownProse';
import { useTranslations } from 'next-intl';
import { twMerge } from 'tailwind-merge';

interface Props {
  result: MultichainExploitResult;
}

const ExploitResultCard = ({ result }: Props) => {
  const t = useTranslations();
  const { exploit, affectedSpenders, previouslyAffectedSpenders, status } = result;

  const affectedChains = deduplicateArray(affectedSpenders.map((spender) => spender.chainId));
  const previouslyAffectedChains = deduplicateArray(previouslyAffectedSpenders.map((spender) => spender.chainId));

  const classeName = twMerge(
    'rounded-lg border p-4 flex flex-col gap-3',
    status === 'affected' ? 'border-red-500/50' : 'border-yellow-500/50',
    status === 'affected' ? 'bg-red-500/5 dark:bg-red-500/10' : 'bg-yellow-500/5 dark:bg-yellow-500/10',
  );

  return (
    <div className={classeName}>
      <div className="flex items-center justify-between gap-4">
        <Href href={`/exploits/${exploit.slug}`} className="text-lg font-bold hover:underline" router>
          {exploit.name}
        </Href>
        <span className="text-sm text-zinc-500 dark:text-zinc-400 shrink-0">{formatDate(exploit.date)}</span>
      </div>

      <div className="text-sm text-zinc-600 dark:text-zinc-400">
        <MarkdownProse content={exploit.description} />
      </div>

      {affectedChains.length > 0 && (
        <ChainLogoRow label={t('exploits.premium.affected.chains_label')} chainIds={affectedChains} />
      )}
      {previouslyAffectedChains.length > 0 && (
        <ChainLogoRow
          label={t('exploits.premium.previously_affected.chains_label')}
          chainIds={previouslyAffectedChains}
        />
      )}
    </div>
  );
};

interface ChainLogoRowProps {
  label: string;
  chainIds: number[];
}

const ChainLogoRow = ({ label, chainIds }: ChainLogoRowProps) => (
  <div className="flex items-center gap-2">
    <span className="text-sm text-zinc-500 dark:text-zinc-400">{label}</span>
    <div className="flex gap-1 flex-wrap">
      {chainIds.map((chainId) => (
        <ChainLogo key={chainId} chainId={chainId} size={20} tooltip />
      ))}
    </div>
  </div>
);

export default ExploitResultCard;
